import styled from "@emotion/styled";
import { Col, Form, Input, message, Modal, Row, Select, Spin } from "antd";
import Button from "atoms/Button";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import {
  addBackPendingEncashment,
  allowNumberKeys,
  getPendingEncashmentDays,
} from "pages/LeaveEncashment/common";
import { find, isArray, result } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import {
  getAllDepartmentList,
  getClientid,
  getLeaveCategoryList,
  getUserInfo,
  getUserList,
} from "redux/selectors";
import { dateInDetail, getKenyanDateTime } from "utils/Date";

const StyledModal = styled(Modal)`
  .ant-modal-header {
    border-bottom: none;
    padding-top: 20px;
    padding-bottom: 30px;
  }
  .ant-modal-title {
    font-weight: bold;
  }
  .ant-modal-body {
    padding-top: 0px;
  }
  .ant-modal-footer {
    display: none;
  }
  .ant-form-item-label {
    font-weight: 500;
  }
  .ant-input-number {
    width: 100%;
  }
  .ant-form-item {
    margin-bottom: 16px;
  }
  .ant-form-item-extra,
  .note {
    font-size: 12px;
    color: #6b7280;
  }
`;

const StyledButton = styled(Button)`
  border-radius: 5px;
  width: initial;
`;

const StyledFooterDiv = styled.div`
  padding-top: 20px;
`;

// Leave categories whose name matches this can be encashed. If the user has
// none of these, every category with a balance is offered instead.
const ENCASHABLE_LEAVE_REGEX = /annual/i;

// "Days to Encash" is a text field: keep digits and the first decimal point
// (covers pasted text), and read it as a number when validating / submitting.
const normalizeDays = (value = "") => {
  const [whole, ...rest] = String(value).replace(/[^0-9.]/g, "").split(".");
  return rest.length ? `${whole}.${rest.join("")}` : whole;
};
const toDays = (value) => (value === "" || value == null ? NaN : Number(value));

function LeaveEncashmentForm({
  isModalVisible,
  closeModal,
  ClientId,
  leaveCategoryList,
  userInfo,
  userList,
  AllDepartmentList,
  // Pass a waiting request to edit it instead of applying for a new one.
  encashment = null,
}) {
  const [form] = Form.useForm();
  const isEdit = !!encashment?.encashment_id;
  const { TextArea } = Input;
  const containerRef = useRef(null);
  const [showLoader, toggleLoader] = useState(false);
  const [balanceLoader, toggleBalanceLoader] = useState(false);
  const [leaveBalances, setLeaveBalances] = useState({});

  const finduser = userList?.find((item) => item?.userid === userInfo?.userid);
  const employeeName = finduser?.fullname || userInfo?.name || "";

  // Same location / gender filtering as LeaveForm.
  const userCategories = useMemo(
    () =>
      (leaveCategoryList || []).filter((item) => {
        if (!item?.locationId?.includes(finduser?.baselocationid)) return false;
        const eligible = item?.eligible?.toLowerCase();
        return finduser?.gender?.toLowerCase() === "male"
          ? eligible !== "female"
          : eligible !== "male";
      }),
    [leaveCategoryList, finduser]
  );

  const encashableCategories = useMemo(() => {
    const withBalance = userCategories.filter(
      (item) => leaveBalances[item.leave_category_id] !== undefined
    );
    const annual = withBalance.filter((item) =>
      ENCASHABLE_LEAVE_REGEX.test(item?.leave_category_name)
    );
    return annual.length ? annual : withBalance;
  }, [userCategories, leaveBalances]);

  const getLeaveBalances = async () => {
    toggleBalanceLoader(true);
    const response = await CALL_API(`employee-leave-details/${ClientId}`, "post", {
      year: dateInDetail(getKenyanDateTime()).year,
    });
    const balances = {};
    if (isArray(response)) {
      const pending = await getPendingEncashmentDays(ClientId, userInfo?.userid);
      addBackPendingEncashment(response, pending).forEach((leave) => {
        const [details] = Object.values(leave);
        const available = isArray(details)
          ? find(details, { type: "available" })
          : null;
        if (available?.leaveCategoryId !== undefined) {
          balances[available.leaveCategoryId] = Number(available.value) || 0;
        }
      });
    }
    setLeaveBalances(balances);
    toggleBalanceLoader(false);
  };

  const resetForm = () => {
    form.setFieldsValue(
      isEdit
        ? {
            leave_type: encashment.leave_category_id,
            number_of_days: String(encashment.number_of_days ?? ""),
            reason: encashment.reason || "",
          }
        : {
            leave_type: encashableCategories[0]?.leave_category_id,
            number_of_days: "",
            reason: "",
          }
    );
  };

  useEffect(() => {
    if (isModalVisible && ClientId) getLeaveBalances();
  }, [isModalVisible, ClientId]);

  useEffect(() => {
    if (isModalVisible) resetForm();
  }, [isModalVisible, encashableCategories, encashment]);

  const leaveType = Form.useWatch("leave_type", form);
  const numberOfDays = Form.useWatch("number_of_days", form);
  // Waiting requests (including the one being edited) are already given back.
  const availableBalance = leaveBalances[leaveType] ?? 0;
  const days = toDays(numberOfDays);
  const canSubmit =
    !!leaveType && days > 0 && Number.isInteger(days * 2) && days <= availableBalance;

  const onClose = () => {
    form.resetFields();
    closeModal(false);
  };

  const onFinish = async ({ leave_type, number_of_days, reason }) => {
    toggleLoader(true);
    const category = find(userCategories, { leave_category_id: leave_type });
    const deptName = result(
      find(AllDepartmentList, (e) =>
        finduser?.basedepartmentid?.includes(e.department_id)
      ),
      "department_name"
    );
    const { code } = await CALL_API(`save-leave-encashment/${ClientId}`, "post", {
      ...(isEdit ? { encashment_id: encashment.encashment_id } : {}),
      leave_category_id: leave_type.toString(),
      leave_category_name: category?.leave_category_name || "",
      number_of_days: toDays(number_of_days),
      available_balance: availableBalance,
      reason: reason || "",
      employeeid: finduser?.employeeid,
      fullname: employeeName,
      reporting_manager: finduser?.reportingmanager,
      department_id: finduser?.basedepartmentid,
      department_name: deptName,
      baselocationid: finduser?.baselocationid,
    });
    toggleLoader(false);
    if (code === STATUS_CODE.SUCCESS) {
      message.success(
        isEdit
          ? "Leave encashment request has been updated"
          : "Leave encashment request has been submitted"
      );
      form.resetFields();
      closeModal(true);
    } else if (code === STATUS_CODE.LEAVE_EXCEED) {
      message.error("You are exceeding your available leave balance");
      getLeaveBalances();
    } else if (code === STATUS_CODE.RECORD_EXIST) {
      message.error(
        isEdit
          ? "This request was already processed, or you have a pending request for that leave type"
          : "You already have a pending leave encashment request"
      );
      if (isEdit) closeModal(true);
    } else if (code === STATUS_CODE.RECORD_NOT_FOUND) {
      message.error("This leave encashment request no longer exists");
      closeModal(true);
    } else {
      message.error("Oops!! something went wrong");
    }
  };

  return (
    <StyledModal
      title={isEdit ? "Edit Leave Encashment" : "Leave Encashment Application"}
      open={isModalVisible}
      onCancel={onClose}
      width={600}
      footer={[]}
      destroyOnClose
    >
      <Spin spinning={balanceLoader}>
        <Form form={form} name="leaveencashmentform" layout="vertical" onFinish={onFinish}>
          <Row gutter={20}>
            <Col xs={24}>
              <Form.Item label="Employee Name">
                <Input value={employeeName} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} ref={containerRef}>
              <Form.Item
                label="Leave Type"
                name="leave_type"
                rules={[{ required: true, message: "Leave type is required" }]}
              >
                <Select
                  placeholder={balanceLoader ? "" : "No leave available to encash"}
                  options={encashableCategories}
                  fieldNames={{
                    label: "leave_category_name",
                    value: "leave_category_id",
                  }}
                  disabled={encashableCategories.length <= 1}
                  onChange={() => form.validateFields(["number_of_days"])}
                  getPopupContainer={() => containerRef.current}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label="Days to Encash"
                name="number_of_days"
                extra={
                  <span>
                    Available balance: <strong>{availableBalance} days</strong>
                  </span>
                }
                normalize={normalizeDays}
                rules={[
                  { required: true, message: "Days to encash is required" },
                  () => ({
                    validator(_, value) {
                      if (value === null || value === undefined || value === "") {
                        return Promise.resolve();
                      }
                      const days = toDays(value);
                      if (!(days > 0)) {
                        return Promise.reject("Days must be greater than 0");
                      }
                      if (!Number.isInteger(days * 2)) {
                        return Promise.reject("Days must be in steps of 0.5");
                      }
                      if (days > availableBalance) {
                        return Promise.reject(
                          `Days cannot exceed your available balance (${availableBalance})`
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  maxLength={5}
                  onKeyDown={allowNumberKeys}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Reason / Comments" name="reason">
                <TextArea maxLength={500} autoSize={{ minRows: 3, maxRows: 6 }} />
              </Form.Item>
              <div className="note">
                Approved leave encashment will be processed in the applicable
                payroll period.
              </div>
            </Col>
          </Row>
          <StyledFooterDiv className="align-right">
            <StyledButton type="primary" onClick={resetForm} disabled={showLoader}>
              Reset All
            </StyledButton>
            <StyledButton
              loading={showLoader}
              type="primary"
              htmlType="submit"
              disabled={!canSubmit}
            >
              {showLoader ? "Submitting" : isEdit ? "Update" : "Submit"}
            </StyledButton>
          </StyledFooterDiv>
        </Form>
      </Spin>
    </StyledModal>
  );
}

const mapStateToProps = (state) => ({
  ClientId: getClientid(state),
  leaveCategoryList: getLeaveCategoryList(state),
  userInfo: getUserInfo(state),
  userList: getUserList(state),
  AllDepartmentList: getAllDepartmentList(state),
});

export default connect(mapStateToProps, null)(LeaveEncashmentForm);
